import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  FORM_STATUS,
  FORM_ACTION,
  FORM_CATEGORY,
  FORM_STATUS_DISPLAY,
} from "../components/employee/Forms/constants";
import { formatDate } from "../utils/dateUtils";

/**
 * Custom hook for fetching and managing employee forms data.
 * Follows the project's established architecture pattern.
 */
export function useEmployeeForms() {
  const [forms, setForms] = useState([]);
  const [pensionForms, setPensionForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getActionForStatus = (status) => {
    switch (status) {
      case FORM_STATUS.NOT_STARTED:
        return FORM_ACTION.START;
      case FORM_STATUS.DRAFT:
        return FORM_ACTION.CONTINUE;
      case FORM_STATUS.SUBMITTED:
        return FORM_ACTION.PREVIEW;
      case FORM_STATUS.APPROVED:
        return FORM_ACTION.PREVIEW;
      case FORM_STATUS.REJECTED:
        return FORM_ACTION.EDIT_RESUBMIT;
      default:
        return FORM_ACTION.START;
    }
  };

  const fetchForms = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch form definitions
      const formsResponse = await axios.get("/v1/employee/forms");
      
      // Fetch employee submissions
      let submissions = [];
      try {
        const submissionsResponse = await axios.get("/v1/employee/submissions");
        submissions = submissionsResponse.data.forms || [];
      } catch (err) {
        // No submissions yet, that's fine
        console.log("No submissions found");
      }

      // Create a map of form_code -> submission
      const submissionMap = {};
      submissions.forEach(sub => {
        submissionMap[sub.form_code] = sub;
      });

      const transformedForms = formsResponse.data.map((form) => {
        const submission = submissionMap[form.code];
        const status = submission ? submission.status : FORM_STATUS.NOT_STARTED;
        
        return {
          id: form.id,
          code: form.code,
          title: form.name,
          description: form.category || "Employee form",
          meta: submission ? `Last updated: ${formatDate(submission.updated_at)}` : "Ready to be filled",
          status: status,
          statusDisplay: FORM_STATUS_DISPLAY[status] || FORM_STATUS_DISPLAY[FORM_STATUS.NOT_STARTED],
          action: getActionForStatus(status),
          document_group: form.document_group,
          icon: "FileText",
          is_mandatory: form.is_mandatory,
          submissionId: submission ? submission.id : null,
        };
      });

      // Group forms by document_group:
      // 'joining' - Joining Forms (PF Nomination, Family Details, etc.)
      // 'pension' - Retirement & Pension Forms
      // 'registration' - Registration documents (not displayed in My Forms)
      setForms(
        transformedForms.filter(
          (form) => form.document_group === "joining",
        ),
      );

      setPensionForms(
        transformedForms.filter(
          (form) => form.document_group === "pension",
        ),
      );
    } catch (err) {
      setError("Failed to load forms");
      console.error("Error loading forms:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  return { forms, pensionForms, loading, error, refetch: fetchForms };
}

export default useEmployeeForms;
